import { Route, Routes, Navigate, useParams } from "react-router-dom";
import {
  // Dashboard, // old bird's-eye home — replaced by POS as Dashboard
  // Reports,
  // FinancialReport,
  AccountantDashboard,
  HomeShop,
  CurrencyTotals,
  // SellingReport,
} from "../../features/dashboard";
import {
  AddProducts,
  GetProducts,
  ProductSaleHistory,
  // Stock, // hidden from app UI
  UpdateProducts,
} from "../../features/products";
import {
  AddCustomer,
  CombinedPayments,
  CustomerOrderDetail,
  CustomerPaymentHistory,
  CustomerView,
  GetCustomer,
  IncreasePayment,
  Payment,
  PaymentRecipt,
  UpdateCustomer,
} from "../../features/customers";
import { CartShop, EditOrder, InfoOrder, OrderView, TableOrder, ReturnsList, CancellationsList, UnpaidSales } from "../../features/orders";
import { CashManagement } from "../../features/cash";
import { GetUsers, GetRoles, NotFound, SystemSettings, TranslationSettings, Maintenance } from "../../features/shared";
import UIDemo from "../../features/demo/UIDemo";
import { FARMER_FINANCE_ACCESS } from "../../features/purchasing/components/FarmerFinanceTabs";
import {
  Vendors,
  VendorView,
  CompanyExpenses,
  FarmerSettlementArchives,
  FarmerSettlementArchiveView,
  FarmerSettlementWorkspace,
  IncreaseVendorOwed,
} from "../../features/purchasing";
import { DailyRegister } from "../../features/pos";
import PermissionGuard from "../../shared/ui/PermissionGuard";
import HomeRedirect from "../../shared/ui/HomeRedirect";

/** Build a redirect that swaps :param tokens from the current URL. */
const RedirectParams = ({ to }) => {
  const params = useParams();
  let path = to;
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, encodeURIComponent(value));
  });
  return <Navigate to={path} replace />;
};

/** Old OrderHistory URL → modern customer profile */
const RedirectCustomerOrderHistory = () => {
  const { id } = useParams();
  return <Navigate to={`/customers/${id}`} replace />;
};

const guarded = (permission, Component, props = {}) => (
  <PermissionGuard permission={permission}>
    <Component {...props} />
  </PermissionGuard>
);

const guardedAny = (permissions, Component, props = {}) => (
  <PermissionGuard any={permissions}>
    <Component {...props} />
  </PermissionGuard>
);

const AppRoutes = ({
  cart,
  handleClick,
}) => {
  return (
    <Routes>
      {/* Land on POS for cashiers, Accountant Dashboard for number-checkers */}
      <Route path="/" element={<HomeRedirect />} />

      {/* ── Products (standard) ─────────────────────────────────────── */}
      <Route path="/products" element={guarded("products.index", GetProducts)} />
      <Route path="/products/new" element={guarded("products.add", AddProducts)} />
      <Route path="/products/:id/edit" element={guarded("products.edit", UpdateProducts)} />
      <Route path="/products/:id/history" element={guarded("products.index", ProductSaleHistory)} />

      {/* Products — old bookmarks */}
      <Route path="/GetProducts" element={<Navigate to="/products" replace />} />
      <Route path="/AddProducts" element={<Navigate to="/products/new" replace />} />
      <Route path="/GetProducts/UpdateProducts/:id" element={<RedirectParams to="/products/:id/edit" />} />
      <Route path="/Stock" element={<Navigate to="/products" replace />} />
      <Route path="/AddUnit" element={<Navigate to="/products" replace />} />
      <Route path="/GetUnit" element={<Navigate to="/products" replace />} />
      <Route path="/GetUnit/UpdateUnit/:id" element={<Navigate to="/products" replace />} />
      <Route path="/categories" element={<Navigate to="/products" replace />} />
      <Route path="/categories/add" element={<Navigate to="/products" replace />} />
      <Route path="/categories/update/:id" element={<Navigate to="/products" replace />} />

      {/* ── Customers (standard) ────────────────────────────────────── */}
      <Route path="/customers" element={guarded("customers.index", GetCustomer)} />
      <Route path="/customers/new" element={guarded("customers.add", AddCustomer)} />
      <Route path="/customers/:id" element={guarded("customers.index", CustomerView)} />
      <Route path="/customers/:id/edit" element={guarded("customers.edit", UpdateCustomer)} />
      <Route path="/customers/:id/pay" element={guarded("customers.payment", Payment)} />
      <Route path="/customers/:id/adjust-balance" element={guarded("maintenance.reset", IncreasePayment)} />
      <Route path="/customers/:id/receipts/:paymentId" element={guardedAny(["customers.index", "customers.payment"], PaymentRecipt)} />
      <Route path="/customers/:id/receipts" element={guardedAny(["customers.index", "customers.payment"], PaymentRecipt)} />
      <Route path="/customers/:id/payments" element={guarded("customers.index", CustomerPaymentHistory)} />
      <Route path="/customers/:id/combined-payments" element={guarded("customers.index", CombinedPayments)} />
      <Route path="/customer/:customerId/order/:orderId" element={guardedAny(["pos.sell", "reports.view", "reports.unpaid_sales", "customers.index"], CustomerOrderDetail)} />

      {/* Customers — old bookmarks */}
      <Route path="/GetCustomer" element={<Navigate to="/customers" replace />} />
      <Route path="/AddCustomer" element={<Navigate to="/customers/new" replace />} />
      <Route path="/GetCustomer/UpdateCustomer/:id" element={<RedirectParams to="/customers/:id/edit" />} />
      <Route path="/GetCustomer/Payment/:id" element={<RedirectParams to="/customers/:id/pay" />} />
      <Route path="/GetCustomer/IncreasePayment/:id" element={<RedirectParams to="/customers/:id/adjust-balance" />} />
      <Route path="/GetCustomer/PaymentRecipt/:id/:paymentId" element={<RedirectParams to="/customers/:id/receipts/:paymentId" />} />
      <Route path="/GetCustomer/PaymentRecipt/:id" element={<RedirectParams to="/customers/:id/receipts" />} />
      <Route path="/GetCustomer/CustomerPaymentHistory/:id" element={<RedirectParams to="/customers/:id/payments" />} />
      <Route path="/GetCustomer/CombinedPayments/:id" element={<RedirectParams to="/customers/:id/combined-payments" />} />
      <Route path="/GetCustomer/OrderHistory/:id" element={<RedirectCustomerOrderHistory />} />

      {/* ── Farmers / vendors ───────────────────────────────────────── */}
      <Route path="/vendors" element={guarded("vendors.index", Vendors)} />
      <Route path="/vendors/:id/increase-owed" element={guarded("maintenance.reset", IncreaseVendorOwed)} />
      <Route path="/vendors/:id" element={guarded("vendors.index", VendorView)} />
      <Route path="/farmer-settlements" element={guardedAny(FARMER_FINANCE_ACCESS, FarmerSettlementWorkspace)} />
      <Route path="/farmer-expenses" element={<Navigate to="/farmer-settlements?tab=charges" replace />} />
      <Route path="/farmer-payments" element={<Navigate to="/farmer-settlements?tab=payments" replace />} />
      <Route path="/company-expenses" element={guardedAny(["company_expenses.view", "company_expenses.add", "company_expenses.cancel", "company_expenses.manage"], CompanyExpenses)} />
      <Route path="/farmer-settlement-archives" element={guarded("vendors.settle", FarmerSettlementArchives)} />
      <Route path="/farmer-settlement-archives/:id" element={guarded("vendors.settle", FarmerSettlementArchiveView)} />
      <Route path="/purchase-orders/new" element={<Navigate to="/vendors" replace />} />
      <Route path="/purchase-orders/:id/edit" element={<Navigate to="/vendors" replace />} />
      <Route path="/purchase-orders" element={<Navigate to="/vendors" replace />} />

      {/* ── Orders / sales (standard) ───────────────────────────────── */}
      <Route path="/orders" element={guardedAny(["pos.sell", "orders.index"], TableOrder)} />
      <Route path="/orders/:id/view" element={guardedAny(["pos.sell", "orders.view", "orders.index", "reports.view", "reports.unpaid_sales"], OrderView)} />
      <Route path="/orders/:id/info" element={guardedAny(["pos.sell", "orders.view", "orders.index", "reports.view", "reports.unpaid_sales"], InfoOrder)} />
      <Route path="/orders/:id/edit" element={guardedAny(["pos.edit", "orders.edit"], EditOrder)} />
      <Route path="/unpaid-sales" element={guarded("reports.unpaid_sales", UnpaidSales)} />
      <Route path="/cash-management" element={guarded("cash.view", CashManagement)} />
      <Route path="/returns" element={guardedAny(["pos.returns_view", "pos.return"], ReturnsList)} />
      <Route path="/cancellations" element={guardedAny(["pos.cancel", "pos.returns_view", "pos.return", "orders.index"], CancellationsList)} />
      <Route path="/shop" element={<PermissionGuard permission="orders.add"><HomeShop handleClick={handleClick} /></PermissionGuard>} />
      <Route path="/cart" element={<PermissionGuard permission="orders.add"><CartShop cart={cart} /></PermissionGuard>} />

      {/* Orders — old bookmarks */}
      <Route path="/TableOrder" element={<Navigate to="/orders" replace />} />
      <Route path="/TableOrder/InfoOrder/:id" element={<RedirectParams to="/orders/:id/info" />} />
      <Route path="/edit-order/:id" element={<RedirectParams to="/orders/:id/edit" />} />
      <Route path="/HomeShop" element={<Navigate to="/shop" replace />} />
      <Route path="/CartShop" element={<Navigate to="/cart" replace />} />

      {/* ── POS Register ────────────────────────────────────────────── */}
      <Route path="/pos/register" element={guardedAny(["pos.sell", "orders.index", "data.daily_register.all"], DailyRegister)} />

      {/* ── Reports ─────────────────────────────────────────────────── */}
      <Route path="/Reports" element={<Navigate to="/reports/accountant" replace />} />
      <Route path="/reports/financial" element={<Navigate to="/reports/accountant" replace />} />
      <Route path="/reports/selling" element={<Navigate to="/reports/accountant" replace />} />
      <Route path="/reports/accountant" element={guarded("reports.view", AccountantDashboard)} />
      <Route path="/audit" element={guarded("reports.audit", AccountantDashboard)} />
      <Route path="/reports/accountant/audit" element={<Navigate to="/audit" replace />} />
      <Route path="/reports/accountant/:metric" element={guarded("reports.view", AccountantDashboard)} />
      <Route path="/reports/farmers-finance" element={<Navigate to="/farmer-settlements" replace />} />
      <Route path="/farmers-finance/:id" element={guarded("vendors.finance", VendorView)} />
      <Route path="/CategoryProductReport" element={<Navigate to="/reports/accountant" replace />} />
      <Route path="/currency-totals" element={guarded("reports.view", CurrencyTotals)} />
      <Route path="/reports/buying" element={<Navigate to="/reports/accountant" replace />} />

      {/* ── Admin ───────────────────────────────────────────────────── */}
      <Route path="/roles" element={guarded("roles.index", GetRoles)} />
      <Route path="/users" element={guarded("users.index", GetUsers)} />
      <Route path="/settings/system" element={guarded("users.index", SystemSettings)} />
      <Route path="/settings/translations" element={guarded("maintenance.reset", TranslationSettings)} />
      <Route
        path="/settings/maintenance"
        element={
          <PermissionGuard any={["maintenance.reset", "users.index"]}>
            <Maintenance />
          </PermissionGuard>
        }
      />

      <Route path="/ui-demo" element={<UIDemo />} />
      <Route path="/404" element={<NotFound />} />
      <Route path="/NotFound" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

export default AppRoutes;
